/**
 * BuildingDocumentsByProjectSection
 *
 * De financiële documenten van een gebouw, gegroepeerd per **renovatieproject**
 * en daarbinnen per **projectonderdeel**.
 *
 * Aanleiding (bug 243 / 140): een opgeladen offerte of bestelbon verscheen wel
 * in de algemene documentenlijst, maar op gebouw → Renovatie stond er geen
 * enkel document — daar was enkel een knop die je wégnavigeerde naar die
 * algemene lijst. Wie een document oplaadde en daarna naar het gebouw keek, zag
 * niets terug.
 *
 * Eén rij = één document binnen één onderdeel. Een document met regels op twee
 * onderdelen verschijnt dus in beide groepen, telkens met het bedrag van *zijn*
 * regels daar. Anders zou hetzelfde bedrag twee keer meetellen, en dat is net
 * wat "bij het juiste projectonderdeel" moet vermijden.
 */

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CaretDown,
  CaretRight,
  CaretUp,
  FileText,
  Receipt,
} from "@phosphor-icons/react";
import { Button } from "components/ui/button";
import { RenovationDocument } from "shared/types/renovation";
import { sumDocumentLineAmounts } from "shared/utils/documentLineAmount";

interface BuildingDocumentsByProjectSectionProps {
  buildingId: string;
  documents: RenovationDocument[];
  totalCount: number;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
}

/** Eén document binnen één onderdeel (of zonder onderdeel). */
interface DocumentRij {
  key: string;
  document: RenovationDocument;
  bedrag: number;
}

interface OnderdeelGroep {
  key: string;
  titel: string;
  /** `null` bij documenten die aan geen onderdeel hangen. */
  partId: number | null;
  rijen: DocumentRij[];
  totaal: number;
}

interface ProjectGroep {
  key: string;
  titel: string;
  projectId: number | null;
  onderdelen: OnderdeelGroep[];
  totaal: number;
  aantal: number;
}

const ZONDER_PROJECT = "__zonder_project__";
const ZONDER_ONDERDEEL = "__zonder_onderdeel__";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("nl-BE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

const formatDate = (date?: Date | string) => {
  if (!date) return "-";
  const d = date instanceof Date ? date : new Date(date);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleDateString("nl-BE");
};

const TYPE_CLASSES: Record<string, string> = {
  Offerte: "text-moss-primary bg-moss-primary/10 border-moss-primary/30",
  Factuur: "text-brick-primary bg-brick-primary/10 border-brick-primary/30",
  Bestelbon: "text-blue-700 bg-blue-50 border-blue-200",
  Contract: "text-purple-700 bg-purple-50 border-purple-200",
};

/**
 * Verdeelt de documenten over project → onderdeel.
 *
 * Een document hangt op twee manieren aan een project: rechtstreeks
 * (`document.projects`) én via het onderdeel van zijn regels
 * (`line.part.project`). Het onderdeel is het fijnste niveau en wint; kent een
 * regel er geen, dan valt het document terug op zijn eigen project, en anders
 * op de groep "Niet aan een project gekoppeld" — die verstoppen zou het
 * oorspronkelijke probleem gewoon verplaatsen.
 */
export const groepeerDocumentenPerProject = (
  documents: RenovationDocument[],
): ProjectGroep[] => {
  const projecten = new Map<string, ProjectGroep>();

  const zorgVoorGroep = (
    projectKey: string,
    projectTitel: string,
    projectId: number | null,
    onderdeelKey: string,
    onderdeelTitel: string,
    partId: number | null,
  ): OnderdeelGroep => {
    let project = projecten.get(projectKey);
    if (!project) {
      project = {
        key: projectKey,
        titel: projectTitel,
        projectId,
        onderdelen: [],
        totaal: 0,
        aantal: 0,
      };
      projecten.set(projectKey, project);
    }
    let onderdeel = project.onderdelen.find((o) => o.key === onderdeelKey);
    if (!onderdeel) {
      onderdeel = {
        key: onderdeelKey,
        titel: onderdeelTitel,
        partId,
        rijen: [],
        totaal: 0,
      };
      project.onderdelen.push(onderdeel);
    }
    return onderdeel;
  };

  for (const document of documents) {
    const lines = document.lines ?? [];
    // Regels per onderdeel — zo krijgt elke groep het bedrag van haar eigen
    // regels en niet het volledige documenttotaal.
    const perPart = new Map<number, typeof lines>();
    const zonderPart: typeof lines = [];
    for (const line of lines) {
      const partId = (line as any).part?.id;
      if (typeof partId === "number") {
        perPart.set(partId, [...(perPart.get(partId) ?? []), line]);
      } else {
        zonderPart.push(line);
      }
    }

    for (const [partId, partLines] of perPart) {
      const part = (partLines[0] as any).part;
      const project = part?.project;
      const projectKey = project?.id ? `p${project.id}` : ZONDER_PROJECT;
      const groep = zorgVoorGroep(
        projectKey,
        project?.title ?? "Niet aan een project gekoppeld",
        project?.id ?? null,
        `o${partId}`,
        part?.title ?? `Onderdeel ${partId}`,
        partId,
      );
      const bedrag = sumDocumentLineAmounts(partLines as any);
      groep.rijen.push({
        key: `${document.id}-${partId}`,
        document,
        bedrag,
      });
      groep.totaal += bedrag;
    }

    // Regels zonder onderdeel (en documenten zonder regels) hangen onder het
    // project van het document zelf.
    if (zonderPart.length > 0 || lines.length === 0) {
      const project = document.projects?.[0];
      const groep = zorgVoorGroep(
        project?.id ? `p${project.id}` : ZONDER_PROJECT,
        project?.title ?? "Niet aan een project gekoppeld",
        project?.id ?? null,
        ZONDER_ONDERDEEL,
        "Niet aan een onderdeel gekoppeld",
        null,
      );
      const bedrag = sumDocumentLineAmounts(zonderPart as any);
      groep.rijen.push({
        key: `${document.id}-geen-onderdeel`,
        document,
        bedrag,
      });
      groep.totaal += bedrag;
    }
  }

  const groepen = Array.from(projecten.values());
  for (const project of groepen) {
    project.totaal = project.onderdelen.reduce((s, o) => s + o.totaal, 0);
    project.aantal = project.onderdelen.reduce((s, o) => s + o.rijen.length, 0);
    // "Niet aan een onderdeel gekoppeld" onderaan, de rest op naam.
    project.onderdelen.sort((a, b) => {
      if (a.key === ZONDER_ONDERDEEL) return 1;
      if (b.key === ZONDER_ONDERDEEL) return -1;
      return a.titel.localeCompare(b.titel);
    });
  }
  groepen.sort((a, b) => {
    if (a.key === ZONDER_PROJECT) return 1;
    if (b.key === ZONDER_PROJECT) return -1;
    return a.titel.localeCompare(b.titel);
  });
  return groepen;
};

export const BuildingDocumentsByProjectSection = ({
  buildingId,
  documents,
  totalCount,
  hasMore,
  isLoadingMore,
  onLoadMore,
}: BuildingDocumentsByProjectSectionProps) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(true);
  const [dichtgeklapt, setDichtgeklapt] = useState<Set<string>>(new Set());

  const groepen = useMemo(
    () => groepeerDocumentenPerProject(documents),
    [documents],
  );

  const toggleProject = (key: string) => {
    setDichtgeklapt((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="flex flex-col gap-3">
      <header className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setIsExpanded((v) => !v)}
          className="flex items-center gap-2 transition-opacity hover:opacity-80"
        >
          {isExpanded ? (
            <CaretUp weight="bold" size={16} className="text-neutral-500" />
          ) : (
            <CaretDown weight="bold" size={16} className="text-neutral-500" />
          )}
          <Receipt weight="duotone" size={18} className="text-moss-primary" />
          <p className="text-lg font-semibold">Financiële documenten</p>
          <span className="text-neutral-secondary-mid text-sm">
            ({documents.length}
            {totalCount > documents.length ? ` van ${totalCount}` : ""})
          </span>
        </button>

        <Button
          size="sm"
          variant="outline"
          onClick={() =>
            navigate(`/renovation/documents?building=${buildingId}`)
          }
        >
          Alle documenten
        </Button>
      </header>

      {isExpanded && (
        <>
          {groepen.length === 0 ? (
            <div className="text-neutral-secondary-mid rounded-lg border border-neutral-200 bg-white p-6 text-center text-sm">
              Nog geen offertes, bestelbonnen of facturen op dit gebouw.
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {groepen.map((project) => {
                const open = !dichtgeklapt.has(project.key);
                return (
                  <div
                    key={project.key}
                    className="overflow-hidden rounded-lg border border-neutral-200 bg-white"
                  >
                    <button
                      type="button"
                      onClick={() => toggleProject(project.key)}
                      className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-neutral-50"
                    >
                      <span className="flex items-center gap-2">
                        {open ? (
                          <CaretDown size={14} weight="bold" className="text-neutral-500" />
                        ) : (
                          <CaretRight size={14} weight="bold" className="text-neutral-500" />
                        )}
                        {project.projectId && (
                          <span className="text-brick-primary text-[10px] font-semibold uppercase tracking-wide">
                            RENO-{project.projectId}
                          </span>
                        )}
                        <span className="text-neutral-primary font-medium">
                          {project.titel}
                        </span>
                        <span className="text-neutral-secondary-mid text-xs">
                          {project.aantal} document
                          {project.aantal === 1 ? "" : "en"}
                        </span>
                      </span>
                      <span className="text-neutral-primary text-sm font-semibold tabular-nums">
                        {formatCurrency(project.totaal)}
                      </span>
                    </button>

                    {open &&
                      project.onderdelen.map((onderdeel) => (
                        <div key={onderdeel.key} className="border-t border-neutral-100">
                          <div className="flex items-center justify-between bg-neutral-50 px-3 py-1.5">
                            <span className="flex items-center gap-2">
                              {onderdeel.partId && (
                                <span className="text-brick-primary text-[10px] font-semibold uppercase tracking-wide">
                                  RENO-O{onderdeel.partId}
                                </span>
                              )}
                              <span className="text-neutral-primary text-sm">
                                {onderdeel.titel}
                              </span>
                            </span>
                            <span className="text-neutral-secondary-mid text-xs tabular-nums">
                              {formatCurrency(onderdeel.totaal)}
                            </span>
                          </div>

                          {onderdeel.rijen.map(({ key, document, bedrag }) => (
                            <div
                              key={key}
                              className="flex items-center gap-3 border-t border-neutral-100 px-3 py-2 text-sm hover:bg-neutral-50"
                            >
                              <span
                                className={`shrink-0 rounded border px-2 py-0.5 text-xs font-medium ${
                                  TYPE_CLASSES[document.type] ??
                                  "border-neutral-200 bg-neutral-50 text-neutral-700"
                                }`}
                              >
                                {document.type}
                              </span>
                              {/* Naar de documentenlijst, gefilterd op ditzelfde
                                  onderdeel (of project). De lijst kent geen
                                  filter op één document, dus dit is het dichtste
                                  wat er bestaat — en meteen de nuttigste
                                  context. */}
                              <button
                                type="button"
                                onClick={() =>
                                  navigate(
                                    [
                                      `/renovation/documents?building=${buildingId}`,
                                      project.projectId ? `projectId=${project.projectId}` : null,
                                      onderdeel.partId ? `projectPartId=${onderdeel.partId}` : null,
                                    ]
                                      .filter(Boolean)
                                      .join("&"),
                                  )
                                }
                                className="text-moss-primary flex min-w-0 items-center gap-1.5 font-medium hover:underline"
                                title="Bekijk de documenten van dit onderdeel"
                              >
                                <FileText size={14} weight="duotone" className="shrink-0" />
                                <span className="truncate">
                                  {document.documentNumber || `Document ${document.id}`}
                                </span>
                              </button>
                              <span className="text-neutral-secondary-mid min-w-0 flex-1 truncate">
                                {document.supplier?.name ?? "-"}
                              </span>
                              <span className="text-neutral-secondary-mid shrink-0 text-xs">
                                {formatDate(document.documentDate)}
                              </span>
                              <span className="text-neutral-primary shrink-0 font-medium tabular-nums">
                                {formatCurrency(bedrag)}
                              </span>
                            </div>
                          ))}
                        </div>
                      ))}
                  </div>
                );
              })}
            </div>
          )}

          {/* De groepering geldt over wat er geladen is. Zonder deze knop zou een
              gebouw met veel facturen stil een deel verzwijgen. */}
          {hasMore && (
            <div className="flex justify-center">
              <Button
                size="sm"
                variant="outline"
                onClick={onLoadMore}
                disabled={isLoadingMore}
              >
                {isLoadingMore
                  ? "Laden..."
                  : `Meer laden (${documents.length} van ${totalCount})`}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
